import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';

const COLOR = '#f59e0b';
const TOP_N = 5;

const TopSparePartsUsageWidget = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const chartData = useMemo(() => {
    const rows = data?.topSparePartUsesGraph || [];
    return rows.slice(0, TOP_N).map((row) => ({
      name: row.name,
      quantityUsed: Number(row.quantityUsed) || 0,
    }));
  }, [data]);

  const isEmpty = chartData.length === 0;

  return (
    <ReportWidget
      title={t('business.inventory.top_spare_parts_usage', 'Top Spare Parts Usage')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.top_spare_parts_usage.empty', 'No spare-part usage to display.')}
      onRefresh={handleRefresh}
    >
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
          <Tooltip
            formatter={(value) => [value, t('business.inventory.quantity_used', 'Quantity Used')]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 12,
            }}
          />
          <Bar dataKey="quantityUsed" fill={COLOR} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default TopSparePartsUsageWidget;
